import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, signal } from "@angular/core";
import { Observable } from "rxjs";

import { environment } from "../../../../environments/environment";
import { ArchivedStatus } from "../../models/Enums/archivedStatus.enum";
import { GenericResponseDto } from "../../models/genericResponseDto.model";

@Injectable({
    providedIn: "root"
})
export class VendorArchiveService {
    apiBaseUrl = `${environment.apiBaseUrl}/VendorArchive`;
    httpOptions = {
        headers: new HttpHeaders({ "Content-Type": "application/json" })
    };

    private selectedYearSignal = signal<number | null>(null);
    public selectedYear = this.selectedYearSignal.asReadonly();

    private archiveStatusSignal = signal<number | null>(null);
    public archiveStatus = this.archiveStatusSignal.asReadonly();

    private isYearLoadingSignal = signal<boolean>(true);
    public isYearLoading = this.isYearLoadingSignal.asReadonly();

    constructor(
        private httpClient: HttpClient
    ) { }

    setSelectedYear(year: number | null): void {
        this.selectedYearSignal.set(year);
    }

    setYearLoading(loading: boolean): void {
        this.isYearLoadingSignal.set(loading);
    }

    getSelectedYear(): number | null {
        return this.selectedYearSignal();
    }

    setArchiveStatus(status: number | null): void {
        this.archiveStatusSignal.set(status);
    }

    getArchiveStatus(): number | null {
        return this.archiveStatusSignal();
    }

    getRenewalStatus() {
        const year = this.getSelectedYear();
        return year !== null && year <= 2025 && (this.archiveStatusSignal() === ArchivedStatus.Accepted
        || this.archiveStatusSignal() === ArchivedStatus.Granted);
    }

    GetVendorArchiveYearList(vendorAccountNumber: string): Observable<GenericResponseDto<number[]>> {
        return this.httpClient.get<GenericResponseDto<number[]>>(
            `${this.apiBaseUrl}/GetVendorArchiveYearList?vendorAccountNumber=${vendorAccountNumber}`,
            this.httpOptions
        );
    }

    getLastPendingStatusYear(vendorAccountNumber: string): Observable<GenericResponseDto<number>> {
        return this.httpClient.get<GenericResponseDto<number>>(
            `${this.apiBaseUrl}/last-pending-status-year/${vendorAccountNumber}`,
            this.httpOptions
        );
    }

    sendVendorAcceptanceEmails(): Observable<GenericResponseDto<boolean>> {
        return this.httpClient.post<GenericResponseDto<boolean>>(
            `${this.apiBaseUrl}/email-acceptance`,
            {},
            this.httpOptions
        );
    }

    updateVendorArchiveStatus(
        request: { vendorAccountNumber: string, isAccepted: boolean, optoutReason?: string }
    ): Observable<GenericResponseDto<boolean>> {
        return this.httpClient.post<GenericResponseDto<boolean>>(
            `${this.apiBaseUrl}/update-archive-status`,
            request,
            this.httpOptions
        );
    }

    resendVendorRenewalStatus(
        request: { vendorAccountNumber: string, archiveYear: number }
    ): Observable<GenericResponseDto<boolean>> {
        return this.httpClient.post<GenericResponseDto<boolean>>(
            `${this.apiBaseUrl}/resend-renewal`,
            request,
            this.httpOptions
        );
    }

    grantVendorRenewalStatus(
        request: { vendorAccountNumber: string, archiveYear: number }[]
    ) {
        return this.httpClient.post<GenericResponseDto<boolean>>(
            `${this.apiBaseUrl}/renewal-granted-bulk`,
            request,
            this.httpOptions
        );
    }

    emailPendingAcceptance(): Observable<GenericResponseDto<boolean>> {
        return this.httpClient.post<GenericResponseDto<boolean>>(
            `${this.apiBaseUrl}/email-pending-acceptance`,
            {},
            this.httpOptions
        );
    }

    renewalResendBulk(
        request: { vendorAccountNumber: string; archiveYear: number }[]
    ): Observable<GenericResponseDto<any>> {
        return this.httpClient.post<GenericResponseDto<any>>(
            `${this.apiBaseUrl}/renewal-resend-bulk`,
            request,
            this.httpOptions
        );
    }

    emailAcceptanceSelectedVendors(
        vendorEmailData: Array<{ vendorAccountNumber: string, email: string, forceRenewal: boolean }>,
        year: number
    ): Observable<GenericResponseDto<boolean>> {
        // Convert array to the format expected by backend, including forceRenewal
        const vendorEmailDictionary: Record<string, string> = {};
        const forceRenewalDictionary: Record<string, boolean> = {};

        vendorEmailData.forEach((item) => {
            vendorEmailDictionary[item.vendorAccountNumber] = item.email;
            forceRenewalDictionary[item.vendorAccountNumber] = item.forceRenewal;
        });

        const requestBody = {
            vendorEmailData: vendorEmailDictionary,
            forceRenewalData: forceRenewalDictionary,
            year
        };
        return this.httpClient.post<GenericResponseDto<boolean>>(
            `${this.apiBaseUrl}/email-acceptance-selected-vendors`,
            requestBody,
            this.httpOptions
        );
    }

    getVendorRenewalExclusionsPaged(pageNumber: number, pageSize: number): Observable<GenericResponseDto<any>> {
        return this.httpClient.get<GenericResponseDto<any>>(
            `${this.apiBaseUrl}/vendor-renewal-exclusions-paged?pageNumber=${pageNumber}&pageSize=${pageSize}`,
            this.httpOptions
        );
    }

    addRenewalExclusion(vendorAccountNumber: string): Observable<GenericResponseDto<boolean>> {
        return this.httpClient.post<GenericResponseDto<boolean>>(
            `${this.apiBaseUrl}/add-renewal-exclusion`,
            JSON.stringify(vendorAccountNumber),
            this.httpOptions
        );
    }

    removeRenewalExclusion(vendorAccountNumber: string): Observable<GenericResponseDto<boolean>> {
        return this.httpClient.post<GenericResponseDto<boolean>>(
            `${this.apiBaseUrl}/remove-renewal-exclusion`,
            JSON.stringify(vendorAccountNumber),
            this.httpOptions
        );
    }
}
